import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Referral } from '../types/referral'
import { ReferralList } from '../components/ReferralList'
import { ReferralDetail } from '../components/ReferralDetail'
import { ReferralOverview } from '../components/ReferralOverview'
import { ProviderDirectory } from '../components/ProviderDirectory'
import { ReferralForm } from '../components/ReferralForm'
import { useAuth } from '../contexts/AuthContext'
import './Referrals.css'

type TabType = 'list' | 'overview' | 'directory'

export function Referrals() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<TabType>('list')
  const [selectedReferral, setSelectedReferral] = useState<Referral | null>(null)
  const [showNewReferral, setShowNewReferral] = useState(false)
  const [patientId, setPatientId] = useState<string | undefined>(
    searchParams.get('patientId') || undefined
  )
  const consultationId = searchParams.get('consultationId') || undefined

  // If patientId is in URL params, show new referral form
  useEffect(() => {
    if (patientId && searchParams.get('patientId')) {
      setShowNewReferral(true)
      setActiveTab('list')
    }
  }, [patientId, searchParams])

  const handleReferralClick = (referral: Referral) => {
    setSelectedReferral(referral)
    setPatientId(referral.patientId)
  }

  const handleBack = () => {
    setSelectedReferral(null)
    setShowNewReferral(false)
  }

  const handleNewReferral = () => {
    setShowNewReferral(true)
    setSelectedReferral(null)
  }

  // If viewing a specific referral
  if (selectedReferral && patientId) {
    return (
      <div className="referrals-page">
        <ReferralDetail
          referralId={selectedReferral.id}
          patientId={patientId}
          onBack={handleBack}
          onUpdate={() => {
            setSelectedReferral(null)
          }}
        />
      </div>
    )
  }

  // If creating a new referral
  if (showNewReferral && patientId) {
    return (
      <div className="referrals-page">
        <div className="referrals-page-header">
          <h1>New Referral</h1>
          <button className="btn-secondary" onClick={handleBack}>
            Cancel
          </button>
        </div>
        <ReferralForm
          patientId={patientId}
          consultationId={consultationId}
          onSuccess={() => {
            setShowNewReferral(false)
            setActiveTab('list')
            // Clear URL params
            navigate('/referrals', { replace: true })
          }}
          onCancel={() => {
            handleBack()
            // Clear URL params
            navigate('/referrals', { replace: true })
          }}
        />
      </div>
    )
  }

  return (
    <div className="referrals-page">
      <div className="referrals-page-header">
        <h1>Referrals</h1>
        {activeTab === 'list' && (
          <button className="btn-primary" onClick={handleNewReferral}>
            New Referral
          </button>
        )}
      </div>

      <div className="referrals-tabs">
        <button
          className={`referrals-tab ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          List
        </button>
        <button
          className={`referrals-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`referrals-tab ${activeTab === 'directory' ? 'active' : ''}`}
          onClick={() => setActiveTab('directory')}
        >
          Provider Directory
        </button>
      </div>

      <div className="referrals-content">
        {activeTab === 'list' && (
          <ReferralList
            referringProviderId={user?.id}
            onReferralClick={handleReferralClick}
          />
        )}

        {activeTab === 'overview' && (
          <ReferralOverview referringProviderId={user?.id} />
        )}

        {activeTab === 'directory' && <ProviderDirectory />}
      </div>
    </div>
  )
}
